import { format, parse, add, isBefore, isAfter } from 'date-fns';

/**
 * Format date string to MM/DD/YYYY format
 */
export function formatDate(dateString: string): string {
  if (!dateString) return '—';
  try {
    const date = parse(dateString, 'yyyy-MM-dd', new Date());
    return format(date, 'MM/dd/yyyy');
  } catch {
    return dateString;
  }
}

/**
 * Calculate the initial date for a menu day based on cycle start date and day number
 * @param cycleStartDate - The start date of the menu cycle (YYYY-MM-DD format)
 * @param dayNumber - The day number (1-based)
 * @returns Formatted date string (MM/DD/YYYY)
 */
export function calculateInitialDate(cycleStartDate: string, dayNumber: number): string {
  if (!cycleStartDate || dayNumber < 1) return '—';
  try {
    const startDate = parse(cycleStartDate, 'yyyy-MM-dd', new Date());
    const initialDate = add(startDate, { days: dayNumber - 1 });
    return format(initialDate, 'MM/dd/yyyy');
  } catch {
    return '—';
  }
}

/**
 * Calculate the next occurrence of a menu day
 * @param cycleStartDate - The start date of the menu cycle (YYYY-MM-DD format)
 * @param dayNumber - The day number in the cycle (1-based)
 * @param daysInMenu - Total number of days in the menu cycle
 * @returns Formatted date string (MM/DD/YYYY)
 */
export function calculateNextOccurrence(
  cycleStartDate: string,
  dayNumber: number,
  daysInMenu: number,
): string {
  if (!cycleStartDate || dayNumber < 1 || daysInMenu < 1) return '—';

  try {
    const startDate = parse(cycleStartDate, 'yyyy-MM-dd', new Date());
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Calculate the initial date for this day
    const initialDate = add(startDate, { days: dayNumber - 1 });

    // If initial date is today or in the future, return it
    if (isAfter(initialDate, today) || initialDate.getTime() === today.getTime()) {
      return format(initialDate, 'MM/dd/yyyy');
    }

    // Otherwise, calculate how many cycles have passed and find the next occurrence
    const daysDiff = Math.floor(
      (today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
    );
    const cyclesPassed = Math.floor(daysDiff / daysInMenu);

    // Next occurrence is current cycle + 1
    const nextOccurrence = add(startDate, {
      days: (cyclesPassed + 1) * daysInMenu + (dayNumber - 1)
    });

    return format(nextOccurrence, 'MM/dd/yyyy');
  } catch {
    return '—';
  }
}
